// Value Indicator — types + aggregation (Phase 3).
//
// The first *multi-dataset* widget: it ranks the biggest model-vs-market edges
// across five betting markets (1X2, match goals, match cards, anytime scorer,
// anytime booking). This is exactly the case the Phase-3 data strategy was
// chosen for — the component reads five `window.BentoData.*` datasets through the
// app-web accessor and hands them here; this file stays pure (types + shaping),
// so it's unit-tested without a DOM. The betting-market types defined here are
// the contracts the market-family widgets will reuse.
//
// "Edge" = the gap between the model's implied probability and Pinnacle's, in
// percentage points (positive = value on our side). Top 8 by edge, descending —
// matching the legacy renderer exactly (the visual A/B guards it).

import { clubFromName } from "../clubs/clubs.js";

/** Home/Draw/Away odds. */
export interface Outcome3 {
  home: number;
  draw: number;
  away: number;
}

/** 1X2 market: model odds + each bookmaker's odds. */
export interface WinnerMarket {
  model: Outcome3;
  bookmakers: Record<string, Outcome3>;
}

/** 1X2 data keyed by fixture. */
export type WinnerData = Record<string, WinnerMarket>;

/** Over/Under odds for a line: `[over, under]`. */
export type OverUnder = readonly [number, number];

/** Odds per goals/cards line ("0.5" | "1.5" | "2.5" | "3.5"). */
export type LineMap = Record<string, OverUnder>;

/** A goals/cards market for one scope (e.g. "Match"): model + bookmakers. */
export interface ScopeMarket {
  model: LineMap;
  bookmakers: Record<string, LineMap>;
}

/** Goals or cards data: fixture → scope ("Match", …) → market. */
export type GoalsCardsData = Record<string, Record<string, ScopeMarket>>;

/** One player's odds row (model + per-bookmaker prices). */
export interface PlayerOdds {
  player: string;
  team: string;
  model: number;
  Pinnacle: number;
  [bookmaker: string]: number | string;
}

/** A player market scope ("Match", "ARS", …) with its market types ("Anytime", …). */
export type PlayerMarketScope = Record<string, PlayerOdds[]>;

/** Player goals/cards data: fixture → scope → market type → rows. */
export type PlayerMarketData = Record<string, Record<string, PlayerMarketScope>>;

/** The five datasets the Value Indicator aggregates. */
export interface ValueIndicatorSources {
  winner: WinnerData;
  goals: GoalsCardsData;
  cards: GoalsCardsData;
  playerGoals: PlayerMarketData;
  playerCards: PlayerMarketData;
}

/** One ranked value opportunity. */
export interface ValueRow {
  /** Market label: "1X2" | "Goals" | "Cards" | "Scorer" | "Booked". */
  market: string;
  outcome: string;
  /** Model (fair) decimal odds. */
  model: number;
  /** Pinnacle decimal odds. */
  pn: number;
  /** Edge in percentage points: implied(model) − implied(pinnacle). */
  edge: number;
}

const DEFAULT_FIXTURE = "Arsenal v Everton";
const GOAL_LINES = ["0.5", "1.5", "2.5", "3.5"] as const;

/** Implied probability (%) from decimal odds. */
function implied(odds: number): number {
  return 100 / odds;
}

/** 3-letter team code, via the club registry (falls back to first 3 letters). */
function abbr(name: string): string {
  const n = name.trim();
  return clubFromName(n)?.short ?? n.substring(0, 3).toUpperCase();
}

/**
 * Rank the biggest model-vs-Pinnacle edges across the five markets; return the
 * top 8 descending. Missing datasets for the fixture fall back to Arsenal v
 * Everton, then are skipped entirely if still absent (defensive — the legacy
 * assumed they existed).
 */
export function shapeValueIndicator(
  fixture: string,
  src: ValueIndicatorSources
): ValueRow[] {
  const fix = fixture || DEFAULT_FIXTURE;
  const [p1 = "", p2 = ""] = fix.split(" v ");
  const t1s = abbr(p1);
  const t2s = abbr(p2);

  const rows: ValueRow[] = [];
  const push = (market: string, outcome: string, model: number, pn: number) =>
    rows.push({ market, outcome, model, pn, edge: 0 });

  // 1X2
  const w1 = src.winner[fix] ?? src.winner[DEFAULT_FIXTURE];
  const pnW = w1?.bookmakers["Pinnacle"];
  if (w1 && pnW) {
    push("1X2", `${t1s} Win`, w1.model.home, pnW.home);
    push("1X2", "Draw", w1.model.draw, pnW.draw);
    push("1X2", `${t2s} Win`, w1.model.away, pnW.away);
  }

  // Match goals / cards (over + under per line)
  const overUnder = (data: GoalsCardsData, label: string) => {
    const scope = (data[fix] ?? data[DEFAULT_FIXTURE])?.["Match"];
    const pn = scope?.bookmakers["Pinnacle"];
    if (!scope || !pn) return;
    for (const ln of GOAL_LINES) {
      const m = scope.model[ln];
      const b = pn[ln];
      if (!m || !b) continue;
      push(label, `O/${ln}`, m[0], b[0]);
      push(label, `U/${ln}`, m[1], b[1]);
    }
  };
  overUnder(src.goals, "Goals");
  overUnder(src.cards, "Cards");

  // Anytime player markets
  const anytime = (data: PlayerMarketData, label: string) => {
    const scope = (data[fix] ?? data[DEFAULT_FIXTURE])?.["Match"];
    for (const r of scope?.["Anytime"] ?? []) {
      push(label, r.player, r.model, r.Pinnacle);
    }
  };
  anytime(src.playerGoals, "Scorer");
  anytime(src.playerCards, "Booked");

  for (const r of rows) r.edge = implied(r.model) - implied(r.pn);
  rows.sort((a, b) => b.edge - a.edge);
  return rows.slice(0, 8);
}

/** Edge formatted with an explicit sign and 1dp, e.g. "+1.3%" / "-0.4%". */
export function edgeText(edge: number): string {
  return `${edge > 0 ? "+" : ""}${edge.toFixed(1)}%`;
}

/** Edge colour: green above +0.3pp, red below −0.3pp, grey in between. */
export function edgeColor(edge: number): string {
  return edge > 0.3 ? "#2ecc71" : edge < -0.3 ? "#e74c3c" : "#888";
}
